import { useState } from 'react';
import { ErroreApi } from '../api/cliente.js';
import {
  type EsitoConferma,
  type OrdineDaConfermare,
  useConferma,
  useFiltriProduzione,
  useOrdiniDaConfermare,
} from '../api/produzione.js';
import { BarraFiltri } from '../componenti/BarraFiltri.js';
import { data, quantita } from '../formati.js';
import { t } from '../i18n/lingua.js';
import { useRicerca } from '../useRicerca.js';

/**
 * Da confermare.
 *
 * La pagina della produzione (Rita, 26 ottobre 2026): gli ordini allo stato 1
 * con i loro articoli, da controllare e portare allo stato 2 — che è il
 * momento in cui l'ordine entra in Business Central. Chi la usa non cambia
 * gli ordini: se qualcosa non va lo dice a chi li ha inseriti, che li
 * corregge prima.
 *
 * Niente prezzi, totali, fatture o documenti: il servizio non li manda.
 * Ogni ordine è una scheda con le righe sotto, perché è proprio sulle righe
 * che si lavora — in un elenco a una riga per ordine gli articoli non si
 * vedrebbero.
 *
 * In cima la ricerca e i filtri, come negli Ordini (Antonio, 26 ottobre):
 * numero PO, cliente, destinazione, date, articolo. Stanno nell'indirizzo e
 * valgono per tutte e due le schede; «Seleziona tutti» sceglie quelli che si
 * vedono, così si confermano in un colpo gli ordini di un cliente.
 */

type Scheda = 1 | 2;

export function Produzione() {
  const [scheda, setScheda] = useState<Scheda>(1);
  const { ricerca, testo, setTesto, filtra } = useRicerca();
  const definizioni = useFiltriProduzione();
  const daConfermare = useOrdiniDaConfermare(1, ricerca);
  const confermati = useOrdiniDaConfermare(2, ricerca);
  const filtrato = Boolean(ricerca.q.trim()) || Object.keys(ricerca.filters).length > 0;
  const elenco = scheda === 1 ? daConfermare : confermati;
  const ordini = elenco.data?.orders ?? [];

  const [scelti, setScelti] = useState<Set<number>>(new Set());
  const [chiedo, setChiedo] = useState(false);
  const [esito, setEsito] = useState<EsitoConferma | null>(null);
  const conferma = useConferma();

  // solo gli ordini ancora in elenco: dopo la conferma quelli passati
  // spariscono, e non devono restare contati
  const sceltiVisibili = ordini.filter((o) => scelti.has(o.id)).map((o) => o.id);
  const tuttiScelti = ordini.length > 0 && sceltiVisibili.length === ordini.length;

  const scegli = (id: number, si: boolean) =>
    setScelti((s) => {
      const nuovo = new Set(s);
      if (si) nuovo.add(id);
      else nuovo.delete(id);
      return nuovo;
    });

  const confermaScelti = () =>
    conferma.mutate(sceltiVisibili, {
      onSuccess: (e) => {
        setEsito(e);
        setChiedo(false);
        // restano scelti solo quelli rimasti indietro, così si vedono
        setScelti(new Set(e.skipped.map((s) => s.id)));
      },
    });

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Da confermare')}</h1>
          <div className="page-sub">
            {t('Gli ordini allo stato 1: controlla gli articoli e portali allo stato 2. Se qualcosa non va, avvisa chi ha inserito l’ordine prima di confermarlo.')}
          </div>
        </div>
      </div>

      <div className="filterbar produzione-filtri">
        <span className="omnisearch">
          <span>⌕</span>
          <input
            className="input"
            style={{ border: 'none', height: 22, padding: 0, background: 'transparent' }}
            placeholder={t('Cerca per PO#, cliente, destinazione')}
            aria-label={t('Cerca per PO#, cliente, destinazione')}
            value={testo}
            onChange={(e) => setTesto(e.target.value)}
          />
        </span>
        <BarraFiltri
          definizioni={definizioni.data?.filters ?? []}
          opzioni={definizioni.data?.options ?? {}}
          valori={ricerca.filters}
          onCambia={filtra}
        />
      </div>

      <div className="tabs" role="tablist">
        <button className="tab" role="tab" aria-selected={scheda === 1} onClick={() => setScheda(1)}>
          {t('Da confermare')}
          {daConfermare.data && <span className="count">{daConfermare.data.orders.length}</span>}
        </button>
        <button className="tab" role="tab" aria-selected={scheda === 2} onClick={() => setScheda(2)}>
          {t('Confermati')}
        </button>
      </div>

      <div className="produzione">
        {scheda === 1 && ordini.length > 0 && (
          <div className="produzione-barra">
            <label className="check">
              <input
                type="checkbox"
                checked={tuttiScelti}
                onChange={(e) => setScelti(e.target.checked ? new Set(ordini.map((o) => o.id)) : new Set())}
              />
              <span>{t('Seleziona tutti')}</span>
            </label>
            <span className="dim">
              {t('{n} selezionati', { n: sceltiVisibili.length.toLocaleString('it-IT') })}
            </span>
            {!chiedo ? (
              <button
                className="btn btn-primary"
                disabled={sceltiVisibili.length === 0 || conferma.isPending}
                onClick={() => {
                  setEsito(null);
                  setChiedo(true);
                }}
              >
                {t('Porta allo stato 2')}
              </button>
            ) : (
              <span className="produzione-conferma">
                <span>
                  {sceltiVisibili.length === 1
                    ? t('Porto allo stato 2 l’ordine selezionato?')
                    : t('Porto allo stato 2 i {n} ordini selezionati?', { n: sceltiVisibili.length })}
                </span>
                <button className="btn btn-primary" disabled={conferma.isPending} onClick={confermaScelti}>
                  {conferma.isPending ? t('Un momento…') : t('Sì, porta allo stato 2')}
                </button>
                <button className="btn" disabled={conferma.isPending} onClick={() => setChiedo(false)}>
                  {t('Annulla')}
                </button>
              </span>
            )}
          </div>
        )}

        {conferma.error && (
          <div className="avviso" role="alert">
            <span className="avviso-segno">▲</span>
            <span>
              {conferma.error instanceof ErroreApi ? conferma.error.message : t('Qualcosa non ha funzionato.')}
            </span>
          </div>
        )}

        {esito && (
          <div className={esito.skipped.length === 0 ? 'avviso avviso-ok' : 'avviso'} role="status">
            <span className="avviso-segno">{esito.skipped.length === 0 ? '✓' : '▲'}</span>
            <span>
              {esito.done === 1
                ? t('1 ordine portato allo stato 2.')
                : t('{n} ordini portati allo stato 2.', { n: esito.done })}
              {esito.skipped.length > 0 && (
                <ul className="produzione-scarti">
                  {esito.skipped.map((s) => (
                    <li key={s.id}>{s.reason}</li>
                  ))}
                </ul>
              )}
            </span>
          </div>
        )}

        {elenco.isLoading && <p className="dim">{t('Un momento…')}</p>}
        {elenco.isError && (
          <div className="avviso" role="alert">
            <span className="avviso-segno">▲</span>
            <span>
              {/* una data scambiata nei filtri lo dice il servizio: meglio la sua frase */}
              {elenco.error instanceof ErroreApi && elenco.error.stato === 400
                ? elenco.error.message
                : t('L’elenco non è arrivato: riprova fra un momento.')}
            </span>
          </div>
        )}
        {elenco.data && ordini.length === 0 && (
          <p className="produzione-vuoto">
            {filtrato
              ? t('Nessun ordine con questi filtri.')
              : scheda === 1
                ? t('Nessun ordine da confermare.')
                : t('Nessun ordine confermato.')}
          </p>
        )}

        {ordini.map((o) => (
          <SchedaOrdine
            key={o.id}
            ordine={o}
            scelto={scheda === 1 ? scelti.has(o.id) : null}
            onScegli={(si) => scegli(o.id, si)}
          />
        ))}
      </div>
    </>
  );
}

function SchedaOrdine({
  ordine: o,
  scelto,
  onScegli,
}: {
  ordine: OrdineDaConfermare;
  /** `null` fra i confermati: lì non si sceglie niente. */
  scelto: boolean | null;
  onScegli: (si: boolean) => void;
}) {
  const dove = [o.destination, o.destinationCity, o.destinationCountry].filter(Boolean).join(' · ');
  return (
    <article className={scelto ? 'card produzione-ordine produzione-scelto' : 'card produzione-ordine'}>
      <header className="produzione-testa">
        {scelto !== null && (
          <input
            type="checkbox"
            aria-label={t('Seleziona l’ordine {numero}', { numero: o.orderNumber })}
            checked={scelto}
            onChange={(e) => onScegli(e.target.checked)}
          />
        )}
        <div className="produzione-chi">
          <div>
            <span className="num strong">{o.orderNumber}</span>
            <span className="dim"> · {data(o.orderDate)}</span>
          </div>
          <div className="strong">{o.customer}</div>
          {dove && <div className="dim">{dove}</div>}
        </div>
        <div className="produzione-date">
          {o.deliveryDateRequested && (
            <div>
              <span className="dim">{t('Consegna richiesta')} </span>
              <span className="num">{data(o.deliveryDateRequested)}</span>
            </div>
          )}
          {scelto === null && (
            // la data dell'ultima modifica, non per forza della conferma: un
            // vettore può aver aggiornato l'ordine dopo
            <div className="dim">{t('Ultima modifica: {quando}', { quando: data(o.updatedAt) })}</div>
          )}
        </div>
      </header>

      {o.notes && <p className="produzione-note">{o.notes}</p>}

      <div className="table-wrap produzione-righe">
        <table className="tabella">
          <thead>
            <tr>
              <th>SKU</th>
              <th>{t('Numero BC')}</th>
              <th>{t('Descrizione')}</th>
              <th className="destra">{t('Quantità')}</th>
            </tr>
          </thead>
          <tbody>
            {o.lines.length === 0 ? (
              <tr>
                <td colSpan={4} className="dim">
                  {t('Nessuna riga.')}
                </td>
              </tr>
            ) : (
              o.lines.map((r) => (
                <tr key={r.id}>
                  <td className="num">{r.sku ?? '—'}</td>
                  <td className="num">{r.bcNumber ?? '—'}</td>
                  <td>
                    {r.description ?? <span className="dim">—</span>}
                    {r.crossReference && (
                      <span className="dim" style={{ marginLeft: 6, fontSize: 11 }}>
                        {t('rif. {codice}', { codice: r.crossReference })}
                      </span>
                    )}
                  </td>
                  <td className="destra num">{quantita(r.quantity)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </article>
  );
}
